import { type JSX, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { LogOut, Menu, UserCircle, X } from 'lucide-react';
import { useNavContext } from '../lib/use-nav-context.ts';
import { useSignOut } from '../lib/use-sign-out.ts';
import { NavList } from './NavList.tsx';
import { cn } from '../lib/utils.ts';

/**
 * Slide-over navigation for below-lg viewports (phones and tablets), rendering
 * the same model as the desktop rail via useNavContext().
 *
 * Why this exists (v7.100): AppSidebar was the only renderer of the case,
 * practice, portal-case and profile navs, and it is display:none below its
 * breakpoint. On a phone the case nav's 13 destinations were absent from the
 * DOM, so navigating between case sections meant returning to the case overview
 * and hunting for a card - and there was no indication of which section you were
 * in. The old header cluster carried the GLOBAL items only, and did so as
 * wrapping pills that consumed several rows of chrome at 375px.
 *
 * Radix Dialog supplies the focus trap, Escape handling, scrim click-to-close
 * and `aria-modal` semantics that a hand-rolled panel would have to re-derive.
 */
export function MobileNavDrawer(): JSX.Element {
  const nav = useNavContext();
  const onSignOut = useSignOut();
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();

  // Close on any route change, not just on a NavList click: the back-links,
  // browser Back, and in-page redirects all move the user without a click on an
  // item we control. Leaving it open would cover the screen they just reached.
  useEffect(() => {
    setOpen(false);
  }, [pathname, search]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          data-testid="mobile-nav-trigger"
          aria-label="Open navigation menu"
          className={cn(
            'inline-flex items-center justify-center rounded-md p-2 text-muted-foreground lg:hidden',
            'hover:bg-accent hover:text-foreground',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-foreground/40 backdrop-blur-[1px]',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          )}
        />
        <Dialog.Content
          data-testid="mobile-nav-drawer"
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex w-[17rem] max-w-[85vw] flex-col',
            'border-r border-border bg-card p-3 shadow-lg',
            'focus:outline-none',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-left',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left',
          )}
        >
          <div className="mb-1 flex items-center justify-between">
            <Dialog.Title className="px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Menu
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                data-testid="mobile-nav-close"
                aria-label="Close navigation menu"
                className={cn(
                  'inline-flex items-center justify-center rounded-md p-2 text-muted-foreground',
                  'hover:bg-accent hover:text-foreground',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                )}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </Dialog.Close>
          </div>
          {/* Description is required for aria-describedby; the nav is self-evident. */}
          <Dialog.Description className="sr-only">
            Navigate between the sections available to you.
          </Dialog.Description>

          {/* A real navigation landmark, so assistive tech announces this as the
              primary nav rather than as anonymous content inside a dialog. It
              does not collide with the rail's identical label: the rail is
              display:none below lg and so is absent from the a11y tree. */}
          <nav aria-label="Primary" className="flex min-h-0 flex-1 flex-col">
            <NavList nav={nav} idSuffix="-mobile" onNavigate={() => setOpen(false)} />
          </nav>

          {/* Account actions. Sign out is lg+ only in the header, so the drawer is its
              only home below that. Profile is reachable at every width from the header
              identity block since 2026-08-19; it is kept here LABELLED, because an
              avatar is not a self-describing destination. */}
          <div className="mt-2 flex flex-col gap-0.5 border-t border-border pt-2">
            <Link
              to="/profile"
              data-testid="nav-profile-mobile"
              onClick={() => setOpen(false)}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground',
                'hover:bg-accent hover:text-foreground',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              )}
            >
              <UserCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              Profile
            </Link>
            <button
              type="button"
              data-testid="sign-out-mobile"
              onClick={() => {
                setOpen(false);
                void onSignOut();
              }}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground',
                'hover:bg-accent hover:text-foreground',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
